/**
 * CostMeasureDetailPanel Component
 * 
 * Detailed view for a single cost measure showing:
 * - Key metrics row (decile, episodes, avg cost, national avg, opportunity)
 * - Cost by Service Category chart (horizontal bar)
 * - Episode Cost vs Risk Score scatter plot
 * - Service Category Analysis table
 * - Patient list sorted by opportunity
 * 
 * @see MIPS_Cost_Dashboard_Measure_Detail_Page_Specification_v1.0
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft,
  Activity,
  Users,
  DollarSign,
  Target,
  TrendingUp,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Eye,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ReferenceLine,
  Cell,
  Legend,
} from 'recharts';
import { PatientDetailModal } from './PatientDetailModal';
import { CategoryDrilldownModal, type CategoryData } from './CategoryDrilldownModal';
import { DecileBar, DriverBadge, ScoreSubmissionOnlyView } from './CostSharedComponents';
import { type CostMeasure, type CostSupplemental, type CostPatient } from '@/lib/cost-calculator-utils';

// ============================================
// Types
// ============================================

interface CostMeasureDetailPanelProps {
  measure: CostMeasure;
  practiceId: string;
  npi?: string;
  onClose: () => void;
}

interface ScatterEpisode {
  episodeId: string;
  mbi: string;
  riskScore: number;
  cost: number;
  expectedCost: number | null;
  costRatio: number | null;
  colorCategory: string;
  riskTier: string;
  excessCost: number | null;
}

interface ScatterData {
  episodes: ScatterEpisode[];
  referenceLine: { startX: number; startY: number; endX: number; endY: number; nationalAvg: number } | null;
  riskTiers: { name: string; min: number; max: number; color: string }[];
  flaggedEpisodes: { mbi: string; riskScore: number; actualCost: number; expectedCost: number; costRatio: number }[];
  nationalAvg: number | null;
  disclaimer: string;
}

// ============================================
// Helper Functions
// ============================================

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function getMeasureTypeBadge(type: string): { label: string; className: string } {
  switch (type) {
    case 'Population':
      return { label: 'Population', className: 'bg-blue-100 text-blue-800' };
    case 'Chronic':
      return { label: 'Chronic', className: 'bg-purple-100 text-purple-800' };
    case 'Acute':
      return { label: 'Acute', className: 'bg-orange-100 text-orange-800' };
    case 'Procedural':
      return { label: 'Procedural', className: 'bg-green-100 text-green-800' };
    default:
      return { label: type, className: 'bg-gray-100 text-gray-800' };
  }
}

function getScatterPointColor(colorCategory: string): string {
  switch (colorCategory) {
    case 'green': return '#22C55E';
    case 'yellow': return '#EAB308';
    case 'orange': return '#F97316';
    case 'red': return '#EF4444';
    default: return '#6B7280';
  }
}

// ============================================
// Component
// ============================================

export function CostMeasureDetailPanel({
  measure,
  practiceId,
  npi,
  onClose,
}: CostMeasureDetailPanelProps) {
  // State
  const [supplemental, setSupplemental] = useState<CostSupplemental[]>([]);
  const [patients, setPatients] = useState<CostPatient[]>([]);
  const [scatterData, setScatterData] = useState<ScatterData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAllPatients, setShowAllPatients] = useState(false);
  const [showFlagged, setShowFlagged] = useState(false);
  
  // Modals
  const [selectedPatientMbi, setSelectedPatientMbi] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<CategoryData | null>(null);

  // Load data (skip when score-only — no patient-level feedback files)
  useEffect(() => {
    if (measure.scoreSource === 'cms_submission_only') {
      setIsLoading(false);
      return;
    }
    loadMeasureData();
  }, [measure.measureId, measure.scoreSource]);

  const loadMeasureData = async () => {
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const headers = { Authorization: `Bearer ${token}` };

      // Load supplemental data for this measure
      const suppUrl = new URL(`${apiUrl}/api/practices/${practiceId}/cost/supplemental`);
      suppUrl.searchParams.set('measureId', measure.measureId);
      if (npi) suppUrl.searchParams.set('npi', npi);
      
      const suppResponse = await fetch(suppUrl.toString(), { headers });
      if (suppResponse.ok) {
        const suppData = await suppResponse.json();
        setSupplemental(suppData);
      }

      // Load patients for this measure
      const patientsUrl = new URL(`${apiUrl}/api/practices/${practiceId}/cost/patients`);
      patientsUrl.searchParams.set('measureId', measure.measureId);
      patientsUrl.searchParams.set('limit', '1000');
      if (npi) patientsUrl.searchParams.set('npi', npi);
      
      const patientsResponse = await fetch(patientsUrl.toString(), { headers });
      if (patientsResponse.ok) {
        const patientsData = await patientsResponse.json();
        setPatients(patientsData);
      }

      // Load scatter data (episodes)
      const scatterUrl = new URL(`${apiUrl}/api/practices/${practiceId}/cost/episodes/${measure.measureId}`);
      if (npi) scatterUrl.searchParams.set('npi', npi);
      
      const scatterResponse = await fetch(scatterUrl.toString(), { headers });
      if (scatterResponse.ok) {
        const scatter = await scatterResponse.json();
        setScatterData(scatter);
      }
    } catch (err) {
      console.error('Error loading measure detail:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (measure.scoreSource === 'cms_submission_only') {
    return <ScoreSubmissionOnlyView measure={measure} onClose={onClose} />;
  }

  // Prepare chart data - show all categories with any opportunity (cost or utilization)
  // Sorted from biggest to lowest opportunity, show up to 15 items for scrolling
  const categoryChartData = supplemental
    .filter(s => !s.qscIsAggregate && s.opportunity > 0)
    .sort((a, b) => (b.opportunity || 0) - (a.opportunity || 0))
    .slice(0, 15)
    .map(s => ({
      name: s.category?.substring(0, 20) || s.qscDisplayName,
      provider: s.providerAvgCost,
      national: s.nationalAvgCost,
      opportunity: s.opportunity,
      fullData: s,
    }));

  // Service category table data (all non-aggregate rows)
  const categoryTableData = supplemental
    .filter(s => !s.qscIsAggregate)
    .sort((a, b) => b.opportunity - a.opportunity);

  // Displayed patients
  const displayedPatients = showAllPatients ? patients : patients.slice(0, 10);

  // Badge info
  const typeBadge = getMeasureTypeBadge(measure.type);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={onClose} className="mb-2 -ml-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Measures
          </Button>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold">{measure.name}</h2>
            <Badge className={typeBadge.className}>{typeBadge.label}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Detailed performance analysis • Click categories or patients for details
          </p>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-4 gap-4">
        {/* Decile Score */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Decile Score</p>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold">
              {measure.decileScore?.toFixed(1) ?? '—'}
            </span>
          </div>
          <div className="mt-2">
            <DecileBar decile={measure.points ?? measure.decileScore ?? 1} />
          </div>
        </div>

        {/* Episodes/Beneficiaries */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
            {measure.unitLabel === 'beneficiaries' ? 'Beneficiaries' : 'Episodes'}
          </p>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-muted-foreground" />
            <span className="text-3xl font-bold">{measure.episodes.toLocaleString()}</span>
          </div>
        </div>

        {/* Average Cost - from Score Submission */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Your Avg Cost</p>
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-muted-foreground" />
            <span className="text-3xl font-bold">{formatCurrency(measure.practiceAvgCost || measure.avgCost)}</span>
          </div>
        </div>

        {/* Benchmark (7.5 decile threshold = nationalMedian) */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Benchmark</p>
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-muted-foreground" />
            <span className="text-3xl font-bold">{formatCurrency(measure.nationalMedian || measure.nationalAvg)}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">7.5 decile threshold</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-6">
        {/* Cost by Service Category Chart - fixed height to match scatter chart */}
        <div className="bg-card border rounded-lg p-4 h-[380px] flex flex-col">
          <div className="flex items-center justify-between mb-3 flex-shrink-0">
            <div>
              <h3 className="font-semibold">Cost by Service Category</h3>
              <p className="text-xs text-muted-foreground">Top 15 by opportunity</p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-blue-500"></span>
                Your Cost
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded bg-gray-400"></span>
                National
              </span>
            </div>
          </div>
          
          {categoryChartData.length > 0 ? (
            <div className="flex-1 overflow-y-auto pr-1 min-h-0">
              <div className="space-y-2">
                {categoryChartData.map((cat, idx) => {
                  const maxValue = Math.max(...categoryChartData.map(c => Math.max(c.provider, c.national)));
                  const providerWidth = (cat.provider / maxValue) * 100;
                  const nationalWidth = (cat.national / maxValue) * 100;
                  return (
                    <div key={idx}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs font-medium text-gray-700 truncate" title={cat.name}>
                          {cat.name}
                        </span>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap ml-2">
                          {formatCurrency(cat.provider)} / {formatCurrency(cat.national)}
                        </span>
                      </div>
                      <div className="relative h-5 bg-gray-100 rounded overflow-hidden">
                        {/* National bar (background) */}
                        <div 
                          className="absolute inset-y-0 left-0 bg-gray-300"
                          style={{ width: `${nationalWidth}%` }}
                        />
                        {/* Your cost bar (foreground) */}
                        <div 
                          className="absolute inset-y-0 left-0 bg-blue-500"
                          style={{ width: `${providerWidth}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              No service category data available
            </div>
          )}
        </div>

        {/* Episode Cost vs Risk Score Scatter */}
        <div className="bg-card border rounded-lg p-4 h-[380px] overflow-hidden">
          <h3 className="font-semibold mb-2">Episode Cost vs Risk Score</h3>
          <p className="text-xs text-muted-foreground mb-2">
            Color by cost ratio • Reference line = expected cost
          </p>
          
          {scatterData && scatterData.episodes.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <ScatterChart margin={{ top: 10, right: 10, bottom: 30, left: 60 }}>
                  <XAxis 
                    type="number" 
                    dataKey="riskScore" 
                    name="HCC Risk Score"
                    domain={[0, 'auto']}
                    tickFormatter={(v) => v.toFixed(1)}
                    label={{ value: 'HCC Risk Score', position: 'bottom', offset: 10 }}
                  />
                  <YAxis 
                    type="number" 
                    dataKey="cost" 
                    name="Episode Cost"
                    tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const ep = payload[0].payload as ScatterEpisode;
                      return (
                        <div className="bg-white border rounded-lg shadow-lg p-3 text-sm">
                          <p className="font-semibold">{ep.mbi}</p>
                          <p>HCC Risk: {ep.riskScore.toFixed(2)}</p>
                          <p>Actual Cost: {formatCurrency(ep.cost)}</p>
                          {ep.expectedCost && (
                            <>
                              <p>Expected: {formatCurrency(ep.expectedCost)}</p>
                              <p className={ep.costRatio && ep.costRatio > 1 ? 'text-red-600' : 'text-green-600'}>
                                Ratio: {ep.costRatio?.toFixed(2)}x
                              </p>
                            </>
                          )}
                        </div>
                      );
                    }}
                  />
                  {scatterData.referenceLine && (
                    <ReferenceLine
                      segment={[
                        { x: 0, y: 0 },
                        { x: scatterData.referenceLine.endX, y: scatterData.referenceLine.endY }
                      ]}
                      stroke="#9CA3AF"
                      strokeDasharray="5 5"
                      label={{ value: 'Expected', position: 'end', fontSize: 10 }}
                    />
                  )}
                  <Scatter name="Episodes" data={scatterData.episodes}>
                    {scatterData.episodes.map((ep, idx) => (
                      <Cell key={idx} fill={getScatterPointColor(ep.colorCategory)} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>

              {/* Legend */}
              <div className="flex items-center justify-center gap-4 mt-2 text-xs">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-green-500" /> ≤1.0x
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-yellow-500" /> 1.0-1.5x
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-orange-500" /> 1.5-2.0x
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-red-500" /> &gt;2.0x
                </span>
              </div>

              {/* Flagged Episodes */}
              {scatterData.flaggedEpisodes.length > 0 && (
                <div className="mt-4">
                  <button
                    onClick={() => setShowFlagged(!showFlagged)}
                    className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700"
                  >
                    <AlertTriangle className="h-4 w-4" />
                    Flagged for Review ({scatterData.flaggedEpisodes.length})
                    {showFlagged ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  
                  {showFlagged && (
                    <div className="mt-2 border rounded-lg overflow-hidden text-sm">
                      <table className="w-full">
                        <thead className="bg-red-50">
                          <tr>
                            <th className="text-left p-2">MBI</th>
                            <th className="text-right p-2">HCC</th>
                            <th className="text-right p-2">Actual</th>
                            <th className="text-right p-2">Expected</th>
                            <th className="text-right p-2">Ratio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scatterData.flaggedEpisodes.slice(0, 5).map((ep, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="p-2 font-mono">{ep.mbi}</td>
                              <td className="p-2 text-right">{ep.riskScore.toFixed(2)}</td>
                              <td className="p-2 text-right">{formatCurrency(ep.actualCost)}</td>
                              <td className="p-2 text-right">{formatCurrency(ep.expectedCost)}</td>
                              <td className="p-2 text-right text-red-600 font-semibold">
                                {ep.costRatio.toFixed(1)}x
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No episode data available for scatter plot
            </div>
          )}
        </div>
      </div>

      {/* Service Category Analysis Table */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-semibold mb-1">Service Category Analysis</h3>
            <p className="text-xs text-muted-foreground">
              Click a row for detailed breakdown
            </p>
          </div>
          {/* Driver Legend - Right aligned */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              At/below national
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              More per episode
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-sky-500"></span>
              In more episodes
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              Both
            </span>
          </div>
        </div>
        
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Category</th>
                <th className="text-right p-3 font-medium" title="Average spend on this category per episode that included it (Medicare payment-standardized)">
                  Your Avg / Episode*
                </th>
                <th className="text-right p-3 font-medium" title="National average spend per episode that included this category (adjusted)">
                  Nat'l Avg / Episode*
                </th>
                <th className="text-right p-3 font-medium" title="Share of your episodes that included this category">Your %</th>
                <th className="text-right p-3 font-medium" title="Share of episodes nationally that included this category">Nat'l %</th>
                <th className="text-center p-3 font-medium" title="Medicare rates are standardized — above-national spend reflects service volume/level within episodes, never price">
                  Driver
                </th>
                <th className="text-right p-3 font-medium">Opportunity</th>
              </tr>
            </thead>
            <tbody>
              {categoryTableData.length > 0 ? (
                categoryTableData.map((cat, idx) => (
                  <tr
                    key={idx}
                    onClick={() => setSelectedCategory(cat as CategoryData)}
                    className="border-t cursor-pointer hover:bg-muted/30 transition-colors"
                  >
                    <td className="p-3 truncate max-w-[200px]" title={cat.category}>
                      {cat.category || cat.qscDisplayName}
                    </td>
                    <td className="p-3 text-right font-mono">
                      {formatCurrency(cat.providerAvgCost)}
                    </td>
                    <td className="p-3 text-right font-mono text-muted-foreground">
                      {formatCurrency(cat.nationalAvgCost)}
                    </td>
                    <td className="p-3 text-right font-mono">
                      {formatPercent(cat.providerPctEpisodes)}
                    </td>
                    <td className="p-3 text-right font-mono text-muted-foreground">
                      {formatPercent(cat.nationalPctEpisodes)}
                    </td>
                    <td className="p-3 text-center">
                      <DriverBadge type={cat.lowVolume ? 'LOW_VOLUME' : cat.driverType} />
                    </td>
                    <td className="p-3 text-right font-semibold text-amber-600">
                      {cat.opportunity > 0 ? formatCurrency(cat.opportunity) : '—'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    No service category data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          *Average across episodes that included this service category. Medicare payment rates
          are standardized — differences reflect the volume and level of services accumulated
          within each episode, not prices. Categories with fewer than 11 episodes are marked
          low volume and not badged.
        </p>
      </div>

      {/* Patient List */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold">Patients (sorted by opportunity)</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Showing {displayedPatients.length} of {patients.length} patients
            </p>
          </div>
          {patients.length > 10 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAllPatients(!showAllPatients)}
            >
              {showAllPatients ? 'Show Less' : 'Show All'}
            </Button>
          )}
        </div>

        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">MBI</th>
                <th className="text-center p-3 font-medium">Gender</th>
                <th className="text-center p-3 font-medium">DOB</th>
                <th className="text-center p-3 font-medium">Age</th>
                <th className="text-center p-3 font-medium">Deceased</th>
                <th className="text-right p-3 font-medium">HCC Risk</th>
                <th className="text-right p-3 font-medium">Total Cost</th>
                <th className="text-right p-3 font-medium">Nat'l Avg</th>
                <th className="text-right p-3 font-medium">Opportunity</th>
                <th className="text-center p-3 font-medium">Flags</th>
                <th className="text-center p-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedPatients.length > 0 ? (
                displayedPatients.map((patient, idx) => (
                  <tr key={idx} className="border-t hover:bg-muted/30">
                    <td className="p-3 font-mono">{patient.mbi}</td>
                    <td className="p-3 text-center">{patient.gender}</td>
                    <td className="p-3 text-center text-sm">
                      {patient.dob ? new Date(patient.dob).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-3 text-center">{patient.age}</td>
                    <td className="p-3 text-center text-sm">
                      {patient.dateOfDeath ? new Date(patient.dateOfDeath).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-3 text-right font-mono">
                      {patient.hccRisk?.toFixed(2) || '—'}
                      {patient.hccPercentile > 0 && (
                        <span className="text-muted-foreground ml-1">
                          ({patient.hccPercentile}%)
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right font-mono">
                      {formatCurrency(patient.totalCost)}
                    </td>
                    <td className="p-3 text-right font-mono text-muted-foreground">
                      {formatCurrency(patient.nationalAvg)}
                    </td>
                    <td className="p-3 text-right font-semibold text-amber-600">
                      {patient.excessCost > 0 ? formatCurrency(patient.excessCost) : '—'}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {patient.hasReadmission && (
                          <Badge variant="destructive" className="text-xs px-1">R</Badge>
                        )}
                        {patient.hasSNF && (
                          <Badge className="bg-amber-600 text-xs px-1">SNF</Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedPatientMbi(patient.mbi)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-muted-foreground">
                    No patient data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <PatientDetailModal
        isOpen={!!selectedPatientMbi}
        onClose={() => setSelectedPatientMbi(null)}
        patientMbi={selectedPatientMbi}
        practiceId={practiceId}
        npi={npi}
      />

      <CategoryDrilldownModal
        isOpen={!!selectedCategory}
        onClose={() => setSelectedCategory(null)}
        category={selectedCategory}
        measureName={measure.name}
      />
    </div>
  );
}

export default CostMeasureDetailPanel;

